import "./AutolinkAddEditDialog.scss";

import { Alert, Button, ButtonGroup, Flex, Text } from "@fluentui/react-northstar";
import FormState, {
  FormData,
  ValidatorDictionary,
  validate,
  validators,
} from "@shopify/react-form-state";
import { getImageCardUrl } from "@teams/cards";
import {
  TeamsApplicationTypes,
  TeamsIntegrationReferenceType,
  TeamsIntegrationSystem,
} from "@teams/types";
import { IntegrationAutoLinkDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";

import { FetchError, isError } from "../../services/ChannelApi";
import { integrationsApi } from "../../services/IntegrationsApi";
import { useTeamsContext } from "../../state/TeamsContextProvider";
import { TextInputField } from "../fields";
import { IntegrationSystemIcon } from "../integrations/IntegrationSystemIcon";
import { PollyDialog } from "../PollyDialog";

type AutolinkAddEditDialogParams = {
  setIsOpen: (isOpen: boolean) => void;
  applicationType: TeamsApplicationTypes;
  referenceType: TeamsIntegrationReferenceType;
  referenceId: string;
  integration?: IntegrationAutoLinkDTO;
};
export const AutolinkAddEditDialog: React.FC<AutolinkAddEditDialogParams> = ({
  setIsOpen,
  applicationType,
  referenceType,
  referenceId,
  integration,
}) => {
  const { teamsContext } = useTeamsContext();
  const [selectedSystem, setSelectedSystem] = useState<TeamsIntegrationSystem>("jira");
  const [serverError, setServerError] = useState<string>();

  const systems: TeamsIntegrationSystem[] = ["jira", "github", "opsgenie", "azure-devops", "other"];
  const buttons = systems.map(system => ({
    key: system,
    content: <IntegrationSystemIcon system={system} />,
    primary: selectedSystem === system,
    onClick: () => setSelectedSystem(system),
  }));

  useEffect(() => {
    if (integration) {
      setSelectedSystem(integration.system);
    }
  }, [integration]);

  const initialValues: AutolinkDetails = {
    prefix: integration?.prefix ?? "",
    url: integration?.url ?? "",
  };

  const onAutolinkSubmit = async (formDetails: FormData<AutolinkDetails>) => {
    const { fields } = formDetails;
    const result = integration
      ? await integrationsApi.updateAutolinkIntegration(
          teamsContext,
          integration.id,
          selectedSystem,
          fields.prefix.value,
          fields.url.value
        )
      : await integrationsApi.addAutolinkIntegration(
          teamsContext,
          referenceId,
          referenceType,
          selectedSystem,
          fields.prefix.value,
          fields.url.value
        );

    if (isError(result)) {
      let errorMessage = (result as FetchError).body?.error;
      if (!errorMessage) {
        errorMessage = "Unknown server error while trying to set autolink";
      }
      setServerError(errorMessage as string);
      return;
    }

    setIsOpen(false);
  };

  return (
    <PollyDialog
      open
      onCancel={() => {
        setIsOpen(false);
      }}
      header="Task Management Reference"
    >
      <Text as="h3">Connect and link tasks for</Text>
      <ButtonGroup buttons={buttons} />

      <FormState
        initialValues={initialValues}
        onSubmit={onAutolinkSubmit}
        validators={getValidator()}
        validateOnSubmit
      >
        {({ fields, submitting, submit }) => {
          const generatePreviewLink = () => {
            const url = fields.url.value.replace("<num>", "1234");
            return (
              <a href={`${url}`} target="_blank" rel="noopener noreferrer">
                {fields.prefix.value}1234
              </a>
            );
          };

          return (
            <>
              <TextInputField
                className="autolink-addedit__prefix"
                field={fields.prefix}
                label="Define reference prefix"
                required
                fluid
                autoFocus
                placeholder={getPlaceholderForSystem(selectedSystem)}
                readOnly={submitting}
              />
              <Text size="small" className="autolink-addedit__help-text">
                {selectedSystem === "jira"
                  ? "The prefix must contain a dash at the end. This prefix appended by a number will generate a link anytime it is entered in a response."
                  : "This prefix appended by a number will generate a link anytime it is entered in a response."}
              </Text>

              <TextInputField
                className="autolink-addedit__url"
                field={fields.url}
                label="Target URL"
                required
                fluid
                placeholder="http://example.com/browse/PREFIX-<num>"
                readOnly={submitting}
              />
              <Text size="small" className="autolink-addedit__help-text">
                The URL must contain &lt;num&gt; for placement of the reference number
              </Text>

              <Text as="h3">Task management link preview</Text>

              <div className="autolink-addedit__preview">
                <Flex gap="gap.small">
                  <img
                    src={getImageCardUrl("AVATAR", applicationType)}
                    height="18"
                    width="18"
                    alt="avatar"
                  />
                  <div>
                    <div className="autolink-addedit__preview-name">Forest Britton</div>
                    <div>
                      Here's an example of you your links will appear {generatePreviewLink()} in
                      standup results.
                    </div>
                  </div>
                </Flex>
              </div>

              {serverError && (
                <Alert danger content={serverError} className="autolink-addedit__server-error" />
              )}

              <Flex hAlign="end" gap="gap.small">
                <Button content="Cancel" onClick={() => setIsOpen(false)} />
                <Button
                  primary
                  content={`${integration ? "Update" : "Save"} Autolink Reference`}
                  onClick={async e => {
                    e.preventDefault();
                    submit();
                  }}
                />
              </Flex>
            </>
          );
        }}
      </FormState>
    </PollyDialog>
  );
};

type AutolinkDetails = {
  prefix: string;
  url: string;
};

const getValidator = (): Partial<ValidatorDictionary<AutolinkDetails>> => {
  return {
    prefix: [
      validators.requiredString("Prefix is required"),
      validate(isValidPrefix, "Prefix must not contain whitespace"),
    ],
    url: [
      validators.requiredString("URL is required"),
      validate(
        isValidAutolinkUrl,
        "A url must begin with http:// or https:// and it must contain <num> for placement"
      ),
    ],
  };
};

const isValidPrefix = (input: string) => {
  return !/\s/gm.test(input);
};

const isValidAutolinkUrl = (input: string) => {
  return new RegExp(String.raw`^https?://.*<num>`).test(input);
};

const getPlaceholderForSystem = (system: TeamsIntegrationSystem) => {
  switch (system) {
    case "jira":
      return "PREFIX-";
    case "github":
      return "#";
    case "azure-devops":
      return "#";
    case "opsgenie":
      return "#";
    case "other":
      return "PREFIX-";
  }
};
